import React, { useMemo } from "react";
import Card from "../primitives/card";
import {
  BettorPickSlipPick,
  BettorRecentPick,
  FormattedOdd,
  GameAlignmentType,
  GameStatus,
  LineType,
  Odd,
  Odd2,
  OddOutcome,
  OddVariant,
  PickOutcomeType,
  RecentPick as RecentPickType,
  Sport,
  TeamBasic,
  formatLineType,
  formatOddVariant,
  formatSport,
  isGameException,
} from "common";
import TeamLogo from "../team/logo";
import { formatDiff, formatDate, formatTime, formatOdd, formatUnits } from "@/utils";
import OddsStatusChip from "../game/odds/status-chip";
import Icon from "../primitives/icon";
import Link from "next/link";
import { createGameDetailSlug, createProfileSlug } from "@/utils/url";
import Timestamp from "../game/timestamp";
import Form from "../primitives/form";
import { useOddsFormat } from "@/utils/providers/odds-format";
import { usePickSlip } from "../pick/create-pick";
import Button from "../primitives/button";
import * as RadixSlider from "@radix-ui/react-slider";
import RelativeTime from "../relative-time";
import Avatar from "../primitives/avatar";

export enum RecentPickState {
  Create,
  Checkout,
  Profile,
}

export default function RecentPick({
  pick,
  state,
  updateComment,
  updateSalePrice,
  changeAlignment,
  remove,
}: {
  pick: RecentPickType;
  state: RecentPickState;
  updateComment?: (comment: string | undefined) => void;
  updateSalePrice?: (salePrice: number | null) => void;
  changeAlignment?: () => void;
  remove?: () => void;
}) {
  const { addPick } = usePickSlip();
  const pickAnimationElement = React.useRef<HTMLDivElement>(null);
  const [comment, setComment] = React.useState(pick.comment || "");
  const [salePrice, setSalePrice] = React.useState(1);
  const [addComment, setAddComment] = React.useState(
    pick.comment && state === RecentPickState.Create ? true : false
  );
  const [addSalePrice, setAddSalePrice] = React.useState(
    pick.sale_price && state === RecentPickState.Create ? true : false
  );

  React.useEffect(() => {
    addComment && updateComment?.(comment);
    addSalePrice && updateSalePrice?.(salePrice);
  }, [comment, salePrice, addSalePrice, addComment]);

  const exception = isGameException(pick.game.status as GameStatus);

  const away: boolean | null =
    pick.alignment !== null ? pick.alignment === GameAlignmentType.Away : null;

  const link =
    state === RecentPickState.Profile
      ? createGameDetailSlug(
          formatSport(pick.sport_type) as Sport,
          pick.game.global_game_id
        )
      : undefined;

  const height: string = addComment || addSalePrice ? "auto" : "h-[120px]";

  const awayTeam = pick.game.teams[0];

  const homeTeam = pick.game.teams[1];

  const awayOdds = useMemo(
    () => pick.odds[GameAlignmentType.Away],
    [pick.odds]
  );
  const homeOdds = useMemo(
    () => pick.odds[GameAlignmentType.Home],
    [pick.odds]
  );

  console.log("recent pick", pick);
  return (
    // <RecentPickOuter link={link}>
    <div className="relative">
      {/* <Link
        href={link || ""}
        className="absolute z-[2] inset-0 w-full h-full bg-transparent"
      ></Link> */}
      <Card
        custom
        dark
        className={`${height} flex justify-center overflow-hidden min-w-[15rem] gap-2 border border-[rgba(151,151,151,0.21)] transition-colors group-hover:border-surface-700 h-auto`}
      >
        <div className="z-[2] relative flex flex-col items-center justify-center gap-1 h-[124px]">
          <div className="flex flex-row items-center gap-1 uppercase text-white text-title-lg-bold">
            <span>{awayTeam.name}</span>{" "}
            <span className="text-label-sm-bold">@</span>{" "}
            <span>{homeTeam.name}</span>
          </div>
          <div className="text-surface-700 text-label-lg-medium uppercase">
            {formatLineType(pick.line, false, true)}
          </div>
          {(pick.game.status === "Scheduled" ||
            pick.game.status === "InProgress") &&
          !exception ? (
            <div className="text-label-lg text-secondary-500 flex flex-row items-center gap-[2px]">
              <div>{formatDate(pick.game.date_time_utc, true)}</div>
              <div className="uppercase">
                {formatTime(pick.game.date_time_utc)} -{" "}
                {pick.game.platform || "N/A"}
              </div>
            </div>
          ) : // <Timestamp dateTime={pick.game.dateTime} platform={pick.game.platform} />
          undefined}
          {state === RecentPickState.Profile &&
            (pick.outcome !== null || exception) && (
              <>
                <OddsStatusChip
                  outcome={pick.outcome as OddOutcome}
                  exception={
                    exception ? (pick.game.status as GameStatus) : undefined
                  }
                  dark
                />
                <>
                  {pick.outcome !== OddOutcome.Push && !exception && (
                    <div className="flex gap-0.5 text-white justify-center items-center text-center">
                      {pick.units_gained && pick.units_gained > 0 ? (
                        <Icon
                          name="carom-up"
                          className="fill-success-300"
                          size="sm"
                        />
                      ) : (
                        <Icon
                          name="carom-down"
                          className="fill-error-300"
                          size="sm"
                        />
                      )}
                      <span className="text-label-lg-bold">
                        {pick.units_gained && formatDiff(pick.units_gained)}
                      </span>
                      <span className="text-label-lg">units</span>
                    </div>
                  )}
                </>
              </>
            )}
          
          {pick.sale_price && state === RecentPickState.Profile && (
            <>
              {/* {pick.outcome === null && !pick.is_self ? ( */}
              {pick.outcome === null ? (
                // Show buy now if the pick hasn't settled and not viewing self.
                <div
                  ref={pickAnimationElement}
                  className="flex flex-row gap-4 items-center mt-1 relative z-[3]"
                >
                  <Button
                    size="xs"
                    onClick={() => {
                      console.log("testing click checkout pick");
                      addPick(pick, pickAnimationElement, true);
                    }}
                  >
                    Buy Now ${pick.sale_price / 100}
                  </Button>
                </div>
              ) : pick.purchased ? (
                <>Purchased for {pick.sale_price}</>
              ) : undefined}
            </>
          )}
          {state === RecentPickState.Create && (
            <div className="flex flex-row gap-4 items-center mt-1">
              {/* <button
                onClick={() => setAddSalePrice(true)}
                className="flex items-center gap-1 text-body-sm-medium text-secondary-500 uppercase focus:outline-none"
              >
                <Icon
                  name="dollar-coin"
                  className={`${
                    addSalePrice ? "text-white" : "text-secondary-500"
                  }`}
                  size="sm"
                />
              </button> */}
              <button
                onClick={() => setAddComment(true)}
                className="flex items-center gap-1 text-body-sm-medium text-secondary-500 uppercase focus:outline-none"
              >
                <Icon
                  name="chat-bubble"
                  className={`${
                    addComment ? "text-white" : "text-secondary-500"
                  }`}
                  size="sm"
                />
              </button>
              <button
                onClick={remove}
                className="flex items-center gap-1 text-body-sm-medium text-secondary-500 uppercase focus:outline-none"
              >
                <Icon name="bin" className="fill-secondary-500" size="sm" />
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-10 xs:gap-3 absolute w-full top-0 z-[1] h-[124px]">
          <div className="relative">
            <TeamOdd
              picked={
                state === RecentPickState.Checkout || away === null
                  ? false
                  : away
              }
              team={awayTeam}
              otherTeam={homeTeam}
              alignment={GameAlignmentType.Away}
              odd={awayOdds}
              lockedIn={state === RecentPickState.Profile}
              changeAlignment={changeAlignment}
            />
            <TeamLogo
              custom
              team={awayTeam}
              className="z-[-1]  absolute w-[150px] object-contain right-[-30%] top-[-50px] bottom-[-50px] my-auto opacity-10 max-w-none pointer-events-none"
              splash
            />
          </div>
          <div className="relative">
            <TeamOdd
              picked={
                state === RecentPickState.Checkout || away === null
                  ? false
                  : !away
              }
              team={homeTeam}
              otherTeam={awayTeam}
              odd={homeOdds}
              alignment={GameAlignmentType.Home}
              lockedIn={state === RecentPickState.Profile}
              changeAlignment={changeAlignment}
            />
            <TeamLogo
              custom
              team={homeTeam}
              className="z-[-1]  absolute w-[150px] object-contain right-[-30%] top-[-50px] bottom-[-50px] my-auto opacity-10 max-w-none pointer-events-none"
              splash
            />
          </div>
        </div>

        {/* {checkout && (
          <Link
          href={createProfileSlug(
            "royce"
          )}
          className="flex flex-col items-center justify-center gap-2"
        >
          <div className="flex flex-row gap-2 items-center mt-2">
            <Avatar
              size="xs"
              image={pick.bettor.avatarUrl}
            />
            <div className="text-label-lg-medium text-white">
              {pick.bettor.displayName}
            </div>
          </div>
          <div className="text-label-lg text-secondary uppercase">
            {pick.record?.wins || 0}-
            {pick.record?.losses || 0}-
            {pick.record?.ties || 0} {pick.sport}{" "}
            Record |{" "}
            {pick.units > 0
              ? `+${pick.units}`
              : pick.units}{" "}
            Units
          </div>
        </Link>
        )} */}
        {pick.created_at && pick.bettor && (
          <div className="flex flex-col items-center justify-center -mt-8 mb-4">
            <div className="flex flex-row gap-1 bg-[#3A3C3F] items-center justify-center relative z-[1] py-1 px-2 rounded-sm mx-auto">
              <Icon name="clock" size="xs" className="fill-secondary-500" />
              <span className="text-label-sm-medium uppercase text-secondary-500">
                Created <RelativeTime date={pick.created_at} short />
              </span>
            </div>
            <div className="flex flex-row justify-center items-center gap-2 mx-auto mt-2">
              <div className="flex flex-row items-center gap-1 text-label-lg-medium text-white">
                <Avatar size="sm" img={pick.bettor.avatarUrl} />
                {pick.bettor.displayName}
              </div>
              <span className="text-label-lg text-secondary-500">{formatUnits(pick.bettor.totalUnits)} Units</span>
            </div>
          </div>
        )}
        {addComment && (
          <div className="flex flex-row gap-4 relative z-[1] px-4 pb-4">
            <div className="w-full">
              <Form>
                <Form.Field name="comment">
                  <Form.Input
                    type="text"
                    placeholder="Write a comment..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    dark
                  />
                </Form.Field>
              </Form>
            </div>
            <button
              onClick={() => {
                setAddComment(false);
                updateComment && updateComment(undefined);
              }}
              className="flex items-center gap-1 text-body-sm-medium text-secondary-500 uppercase focus:outline-none"
            >
              <Icon name="bin" className="fill-secondary-500" size="sm" />
            </button>
          </div>
        )}
        {addSalePrice && (
          <form>
            <div className="flex flex-row gap-4 relative z-[1] pb-8 px-4">
              <RadixSlider.Root
                className="relative flex h-5 w-full touch-none select-none items-center"
                value={[salePrice]}
                onValueChange={(v) => setSalePrice(v[0])}
                min={1}
                max={25}
                step={1}
              >
                <RadixSlider.Track className="relative h-[2px] grow rounded-full bg-white">
                  <RadixSlider.Range className="absolute h-full rounded-full bg-primary" />
                  <span className="text-white/50 absolute left-0 top-2 text-label">
                    $1
                  </span>
                  <span className="text-white/50 absolute right-0 top-2 text-label">
                    $25
                  </span>
                </RadixSlider.Track>

                <RadixSlider.Thumb
                  className="relative bg-white rounded-full w-5 h-5 block"
                  aria-label="Volume"
                >
                  <span className="text-white absolute top-6 text-label-bold">
                    ${salePrice}
                  </span>
                </RadixSlider.Thumb>
              </RadixSlider.Root>
              {/* <Form.Field name="salePrice" className="flex min-w-[80px]">
                <Form.Label>Sale Price</Form.Label>
                <Form.Input
                  type="number"
                  placeholder=""
                  min={1}
                  max={100}
                  value={salePrice}
                  onChange={(e) => setSalePrice(Number(e.target.value))}
                  dark
                >
                  Sale Price
                </Form.Input>
              </Form.Field> */}
              <button
                onClick={() => {
                  setAddSalePrice(false);
                  updateSalePrice && updateSalePrice(null);
                }}
                className="flex items-center gap-1 text-body-sm-medium text-secondary-500 uppercase focus:outline-none"
              >
                <Icon name="bin" className="fill-secondary-500" size="sm" />
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>

    // </RecentPickOuter>
  );
}

function TeamOdd({
  team,
  otherTeam,
  odd,
  alignment,
  picked,
  lockedIn = true,
  changeAlignment,
}: {
  team: TeamBasic;
  otherTeam: TeamBasic;
  odd: Odd2;
  alignment: GameAlignmentType;
  picked: boolean;
  lockedIn?: boolean;
  changeAlignment?: () => void;
}) {
  const { oddsFormat } = useOddsFormat();

  odd.alignment = alignment;
  const formattedOdd = formatOdd(oddsFormat, odd);
  if (!lockedIn) {
    return (
      <button
        onClick={changeAlignment}
        disabled={picked}
        className={`relative flex flex-col justify-center items-center px-3 py-[6px] rounded-md min-w-[94px] ${
          picked ? "pickBounce-animation" : ""
        }`}
      >
        <InnerTeamOdd
          picked={picked}
          lockedIn={lockedIn}
          team={team}
          otherTeam={otherTeam}
          isTotal={odd.type === LineType.Total}
          formattedOdd={formattedOdd}
          changeAlignment={changeAlignment}
        />
      </button>
    );
  } else {
    return (
      <div
        className={`relative flex flex-col justify-center items-center px-3 py-[6px] rounded-md min-w-[94px] ${
          picked ? "pickBounce-animation" : ""
        }`}
      >
        <InnerTeamOdd
          picked={picked}
          lockedIn={lockedIn}
          team={team}
          otherTeam={otherTeam}
          isTotal={odd.type === LineType.Total}
          formattedOdd={formattedOdd}
          changeAlignment={changeAlignment}
        />
      </div>
    );
  }
}

function InnerTeamOdd({
  picked,
  lockedIn,
  team,
  otherTeam,
  isTotal,
  formattedOdd,
  changeAlignment,
}: {
  picked: boolean;
  lockedIn: boolean;
  team: TeamBasic;
  otherTeam: TeamBasic;
  isTotal: boolean;
  formattedOdd?: FormattedOdd;
  changeAlignment?: () => void;
}) {
  const rotationAngle = React.useMemo(() => {
    return Math.random() * 360 - 180;
  }, [team, otherTeam]);
  return (
    <>
      {!picked && changeAlignment && (
        <div className="absolute inset-0 rounded-md opacity-40 hover:scale-105 transition-transform"></div>
      )}

      {picked && lockedIn && (
        <svg
          width="135"
          height="136"
          viewBox="0 0 135 136"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute z-0"
          style={{ transform: `rotate(${rotationAngle}deg)` }}
        >
          <g filter="url(#filter0_d_12594_489044)">
            <path
              d="M39.2684 32.6626C39.2797 32.6739 37.155 34.2901 34.0525 37.1608C30.9557 40.0316 26.8926 44.2133 23.3042 49.3784C21.5071 51.9552 19.8345 54.7639 18.4386 57.6967C17.0371 60.6239 15.9238 63.6755 15.1554 66.6423C14.3812 69.6148 13.9573 72.5081 13.8048 75.102C13.6522 77.6959 13.7596 79.9958 13.9743 81.8097C14.4038 85.5225 15.3814 89.5177 17.0541 93.5357C18.7211 97.5535 21.089 101.588 24.1518 105.375C27.209 109.155 30.9612 112.681 35.2956 115.71C39.6299 118.739 44.5519 121.271 49.8982 123.062C55.2328 124.854 60.9913 125.899 66.8734 125.944C72.7561 125.995 78.7575 125.029 84.4591 122.961C90.161 120.898 95.5409 117.739 100.293 113.732C105.051 109.726 109.193 104.9 112.714 99.6162C116.234 94.3325 119.388 88.7944 121.806 82.9347C124.225 77.0858 125.903 70.898 126.361 64.5403C126.593 61.3701 126.519 58.1602 126.101 54.9843C125.689 51.8085 124.937 48.6665 123.858 45.6261C121.71 39.5457 118.241 33.9285 113.968 29.1139C109.691 24.288 104.633 20.2418 99.2138 16.9868C93.7832 13.7318 87.9909 11.2567 82.0795 9.55004C70.2688 6.13677 57.9274 5.77511 46.8503 8.60632C41.3123 10.0191 36.1133 12.2343 31.5532 15.0712C26.9872 17.9023 23.0766 21.3494 19.946 25.0452C16.8097 28.741 14.4532 32.6628 12.7522 36.449C11.0456 40.2465 9.97184 43.9083 9.29378 47.2481C8.61573 50.5879 8.33311 53.6111 8.23138 56.2332C8.13531 58.8609 8.22573 61.0931 8.36135 62.8958C8.64956 66.5069 9.06773 68.4169 9.05643 68.4225L10.9326 68.4847C10.9496 68.4847 10.5314 66.5237 10.311 62.9411C10.2093 61.1553 10.1641 58.9571 10.3223 56.4085C10.4805 53.8655 10.8422 50.9666 11.5825 47.8245C12.3284 44.6882 13.4587 41.2976 15.1597 37.8731C16.855 34.4542 19.1323 31.0014 22.0426 27.8368C24.9472 24.6722 28.496 21.8184 32.514 19.558C36.5318 17.2919 41.0132 15.6306 45.6866 14.6924C52.0893 13.404 58.6728 13.1158 65.1834 13.6865C71.6939 14.2572 78.1468 15.6813 84.3011 17.9079C90.4494 20.1345 96.3152 23.169 101.537 26.9891C106.753 30.8091 111.307 35.4373 114.681 40.7155C118.066 45.9822 120.208 51.882 120.903 57.9568C121.615 64.0373 120.914 70.2704 119.168 76.2774C117.427 82.2959 114.681 88.0994 111.37 93.581C110.957 94.2648 110.533 94.9486 110.098 95.6211L109.437 96.6496L108.787 97.6329C107.911 98.9326 107.001 100.193 106.052 101.413C104.153 103.855 102.107 106.132 99.9205 108.2C95.5466 112.343 90.585 115.631 85.273 117.835C79.9666 120.051 74.3213 121.186 68.7159 121.283C63.1101 121.384 57.5608 120.474 52.3845 118.768C47.2082 117.061 42.3991 114.575 38.1778 111.534C33.9564 108.5 30.3228 104.917 27.4238 101.024C24.5192 97.1357 22.3549 92.937 20.976 88.6987C19.5859 84.4548 18.9812 80.177 19.0151 76.1026C19.0433 72.0225 19.7045 68.1515 20.7443 64.614C21.3659 62.4779 22.4396 59.2907 24.1123 55.8436C25.768 52.3908 28.0171 48.7063 30.4358 45.4457C32.8375 42.1737 35.3634 39.3256 37.2905 37.3025C39.2118 35.2795 40.5117 34.0701 40.506 34.0645L39.2684 32.6626Z"
              fill="#B1ED40"
            />
          </g>
          <defs>
            <filter
              id="filter0_d_12594_489044"
              x="0.191406"
              y="0.710938"
              width="134.301"
              height="135.234"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="2" />
              <feGaussianBlur stdDeviation="4" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.33 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_12594_489044"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_12594_489044"
                result="shape"
              />
            </filter>
          </defs>
        </svg>
      )}
      {picked && !lockedIn && (
        <svg
          width="135"
          height="136"
          viewBox="0 0 135 136"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute z-0"
          style={{ transform: `rotate(${rotationAngle}deg)` }}
        >
          <g filter="url(#filter0_d_12594_489044)">
            <path
              d="M39.2684 32.6626C39.2797 32.6739 37.155 34.2901 34.0525 37.1608C30.9557 40.0316 26.8926 44.2133 23.3042 49.3784C21.5071 51.9552 19.8345 54.7639 18.4386 57.6967C17.0371 60.6239 15.9238 63.6755 15.1554 66.6423C14.3812 69.6148 13.9573 72.5081 13.8048 75.102C13.6522 77.6959 13.7596 79.9958 13.9743 81.8097C14.4038 85.5225 15.3814 89.5177 17.0541 93.5357C18.7211 97.5535 21.089 101.588 24.1518 105.375C27.209 109.155 30.9612 112.681 35.2956 115.71C39.6299 118.739 44.5519 121.271 49.8982 123.062C55.2328 124.854 60.9913 125.899 66.8734 125.944C72.7561 125.995 78.7575 125.029 84.4591 122.961C90.161 120.898 95.5409 117.739 100.293 113.732C105.051 109.726 109.193 104.9 112.714 99.6162C116.234 94.3325 119.388 88.7944 121.806 82.9347C124.225 77.0858 125.903 70.898 126.361 64.5403C126.593 61.3701 126.519 58.1602 126.101 54.9843C125.689 51.8085 124.937 48.6665 123.858 45.6261C121.71 39.5457 118.241 33.9285 113.968 29.1139C109.691 24.288 104.633 20.2418 99.2138 16.9868C93.7832 13.7318 87.9909 11.2567 82.0795 9.55004C70.2688 6.13677 57.9274 5.77511 46.8503 8.60632C41.3123 10.0191 36.1133 12.2343 31.5532 15.0712C26.9872 17.9023 23.0766 21.3494 19.946 25.0452C16.8097 28.741 14.4532 32.6628 12.7522 36.449C11.0456 40.2465 9.97184 43.9083 9.29378 47.2481C8.61573 50.5879 8.33311 53.6111 8.23138 56.2332C8.13531 58.8609 8.22573 61.0931 8.36135 62.8958C8.64956 66.5069 9.06773 68.4169 9.05643 68.4225L10.9326 68.4847C10.9496 68.4847 10.5314 66.5237 10.311 62.9411C10.2093 61.1553 10.1641 58.9571 10.3223 56.4085C10.4805 53.8655 10.8422 50.9666 11.5825 47.8245C12.3284 44.6882 13.4587 41.2976 15.1597 37.8731C16.855 34.4542 19.1323 31.0014 22.0426 27.8368C24.9472 24.6722 28.496 21.8184 32.514 19.558C36.5318 17.2919 41.0132 15.6306 45.6866 14.6924C52.0893 13.404 58.6728 13.1158 65.1834 13.6865C71.6939 14.2572 78.1468 15.6813 84.3011 17.9079C90.4494 20.1345 96.3152 23.169 101.537 26.9891C106.753 30.8091 111.307 35.4373 114.681 40.7155C118.066 45.9822 120.208 51.882 120.903 57.9568C121.615 64.0373 120.914 70.2704 119.168 76.2774C117.427 82.2959 114.681 88.0994 111.37 93.581C110.957 94.2648 110.533 94.9486 110.098 95.6211L109.437 96.6496L108.787 97.6329C107.911 98.9326 107.001 100.193 106.052 101.413C104.153 103.855 102.107 106.132 99.9205 108.2C95.5466 112.343 90.585 115.631 85.273 117.835C79.9666 120.051 74.3213 121.186 68.7159 121.283C63.1101 121.384 57.5608 120.474 52.3845 118.768C47.2082 117.061 42.3991 114.575 38.1778 111.534C33.9564 108.5 30.3228 104.917 27.4238 101.024C24.5192 97.1357 22.3549 92.937 20.976 88.6987C19.5859 84.4548 18.9812 80.177 19.0151 76.1026C19.0433 72.0225 19.7045 68.1515 20.7443 64.614C21.3659 62.4779 22.4396 59.2907 24.1123 55.8436C25.768 52.3908 28.0171 48.7063 30.4358 45.4457C32.8375 42.1737 35.3634 39.3256 37.2905 37.3025C39.2118 35.2795 40.5117 34.0701 40.506 34.0645L39.2684 32.6626Z"
              fill="#B1ED40"
            />
          </g>
          <defs>
            <filter
              id="filter0_d_12594_489044"
              x="0.191406"
              y="0.710938"
              width="134.301"
              height="135.234"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="2" />
              <feGaussianBlur stdDeviation="4" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.33 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_12594_489044"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_12594_489044"
                result="shape"
              />
            </filter>
          </defs>
        </svg>
      )}

      {isTotal ? (
        <div className="w-[45px] h-[33px] relative mb-1">
          <div
            style={{
              clipPath: "polygon(0 0, 63% 0, 35% 100%, 0 100%)",
            }}
            className="absolute inset-0"
          >
            <TeamLogo
              custom
              team={team}
              className="h-[31px] w-[31px] z-[1] absolute left-0"
            />
          </div>
          <svg
            width="12"
            height="31"
            viewBox="0 0 12 31"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-0 right-0 mx-auto z-10"
          >
            <path d="M11.5 0.5L0.5 30" stroke="#A3A8B1" strokeLinecap="round" />
          </svg>

          <div
            style={{
              clipPath: "polygon(63% 0, 100% 0, 100% 100%, 35% 100%)",
            }}
            className="absolute inset-0"
          >
            <TeamLogo
              custom
              team={otherTeam}
              className="h-[31px] w-[31px] z-[1] absolute right-0 top-px"
            />
          </div>
        </div>
      ) : (
        <TeamLogo custom team={team} className="h-[36px] w-[36px] z-[1]" />
      )}
      <div
        className={`text-title-sm tracking-[0.0175rem] ${
          picked ? "text-white" : "text-white"
        }`}
      >
        {isTotal
          ? `${team.abbreviation} / ${otherTeam.abbreviation}`
          : team.abbreviation}
      </div>
      <div>
        <span
          className={`text-title-bold ${picked ? "text-white" : "text-white"}`}
        >
          {formattedOdd?.type === 0
            ? formattedOdd?.payout
            : formattedOdd?.value}
        </span>{" "}
        {formattedOdd?.payout && (
          <span
            className={`text-label-lg-bold uppercase  ${
              !picked ? "text-[#757575]" : "text-white"
            }`}
          >
            {formattedOdd?.type === 0 ? undefined : formattedOdd?.payout}
          </span>
        )}
      </div>
    </>
  );
}
